import React, { Component } from "react";
import { TenantContractInfo } from "@eres/ejari-api/Entity/tenant";
import Strings from "@my-dld/assets/strings";
import { TenantRequestAction, TenantRequestStatus, TenantRequestUserType } from "@eres/ejari-core/constants";
import ConfirmationPopup from "@my-dld/components/Popup/ConfirmationPopup";
import AddRemarks from "./AddRemarks";
import { hideLoading, showErrorMessage, showLoading } from "@my-dld/utils/UIUtils";
import { TenantContractContext } from "@eres/ejari-core/contexts/TenantContractContext";
import { trackEventWithData } from "@my-dld/utils/Analytics";

interface Props {
	userTypeId: string;
	contract: TenantContractInfo;
	onActionComplete: () => void;
}

interface State {
	confirmReject?: boolean;
	showAddRemarks?: boolean;
	status?: number;
}

class OwnerRequestActions extends Component<Props, State> {
	static contextType = TenantContractContext;
	context!: React.ContextType<typeof TenantContractContext>;

	constructor( props: Props ) {
		super( props );
		this.state = {};
	}

	render() {
		const { userTypeId, contract } = this.props;
		const { confirmReject, showAddRemarks } = this.state;
		if ( userTypeId === "" + TenantRequestUserType.OWNER && contract.Status === TenantRequestStatus.PENDING ) {
			return <div className="mb-3 text-end">
				<button
					className="btn btn_1 me-2" type="button"
					onClick={ this.onAccept }>{ Strings.accept }</button>
				<button
					className="btn btn_1 me-2" type="button"
					onClick={ this.onRequestModification }>{ Strings.req_modification }</button>
				<button
					className="btn btn-grey me-2" type="button"
					onClick={ this.showRejectConfirmation }>{ Strings.reject }</button>
				{ confirmReject && <ConfirmationPopup
					message={ Strings.reject_request }
					onConfirm={ this.onConfirmReject }
					onCancel={ this.hideRejectConfirmation }/> }
				{ showAddRemarks && <AddRemarks
					onSubmit={ this.onRemarksSubmit }
					onCancel={ this.hideAddRemarks }/> }
			</div>;
		}
		return null;
	}

	private onAccept = () => {
		const { contract } = this.props;
		this.setState( {
			status: TenantRequestStatus.PAYMENT_PENDING,
		}, () => {
			showLoading();
			if ( contract.RequestActionType === TenantRequestAction.TERMINATE ) {
				this.updateTerminateAction();
			} else {
				this.updateAction();
			}
		} );
	};

	private showRejectConfirmation = () => {
		this.setState( { confirmReject: true } );
	};

	private hideRejectConfirmation = () => {
		this.setState( { confirmReject: false } );
	};

	private onConfirmReject = () => {
		this.setState( {
			status: TenantRequestStatus.REJECTED,
			showAddRemarks: true
		} );
	};

	private onRequestModification = () => {
		this.setState( {
			status: TenantRequestStatus.REQUEST_FOR_UPDATE,
			showAddRemarks: true
		} );
	};

	private hideAddRemarks = () => {
		this.setState( {
			status: undefined,
			showAddRemarks: false
		} );
	};

	private onRemarksSubmit = ( remarks: string ) => {
		showLoading();
		const { contract } = this.props;
		if ( contract.RequestActionType === TenantRequestAction.TERMINATE ) {
			this.updateTerminateAction( remarks );
		} else {
			this.updateAction( remarks );
		}
	};

	private updateAction( remarks?: string ) {
		const { userTypeId, onActionComplete } = this.props;
		const { status } = this.state;
		this.trackEvent();
		this.context.updateTenancyRequestAction( this.context, userTypeId, status!, remarks ).then( () => {
			this.trackEvent( true );
			hideLoading();
			onActionComplete();
		} ).catch( e => {
			this.trackEvent( false );
			hideLoading();
			showErrorMessage( e.toString() );
		} );
	}

	private updateTerminateAction( remarks?: string ) {
		const { userTypeId, onActionComplete } = this.props;
		const { status } = this.state;
		this.trackEvent();
		this.context.updateTenancyTerminateRequestAction( this.context, userTypeId, status!, remarks ).then( () => {
			this.trackEvent( true );
			hideLoading();
			onActionComplete();
		} ).catch( e => {
			this.trackEvent( false );
			hideLoading();
			showErrorMessage( e.toString() );
		} );
	}

	private trackEvent( isSuccess?: boolean ) {
		const { contract } = this.props;
		const { status } = this.state;
		let result = "Try";
		if ( isSuccess !== undefined ) {
			result = isSuccess ? "Success" : "Failed";
		}
		switch ( status ) {
			case TenantRequestStatus.PAYMENT_PENDING:
				trackEventWithData( "TenantContract", "OwnerAccept" + result,
					{ actionType: contract.RequestActionType } );
				break;

			case TenantRequestStatus.REJECTED:
				trackEventWithData( "TenantContract", "OwnerReject" + result,
					{ actionType: contract.RequestActionType } );
				break;

			case TenantRequestStatus.REQUEST_FOR_UPDATE:
				trackEventWithData( "TenantContract", "OwnerReqForModify" + result,
					{ actionType: contract.RequestActionType } );
				break;
		}
	}
}

export default OwnerRequestActions;
